import type { PropsWithChildren, ReactNode } from "react";

import { useAuthStore } from "./authStore";
import { hasAnyGlobalRole, hasJournalRole, isActiveMember } from "./permissions";
import type { SessionMemberProfile } from "./sessionTypes";

export interface RoleGateProps extends PropsWithChildren {
  requireGlobalRoles?: string[];
  requireJournalRoles?: string[];
  requireActiveMember?: boolean;
  when?: (ctx: {
    globalRoles: string[];
    journalRoles: string[];
    memberProfile: SessionMemberProfile | null;
    memberActive: boolean;
  }) => boolean;
  fallback?: ReactNode;
}

export function RoleGate({
  requireGlobalRoles = [],
  requireJournalRoles = [],
  requireActiveMember = false,
  when,
  fallback = null,
  children,
}: RoleGateProps) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const globalRoles = useAuthStore((s) => s.globalRoles);
  const journalMemberships = useAuthStore((s) => s.journalMemberships);
  const memberProfile = useAuthStore((s) => s.memberProfile);
  if (!accessToken) return <>{fallback}</>;

  const memberActive = isActiveMember(memberProfile);

  if (requireGlobalRoles.length && !hasAnyGlobalRole(globalRoles, requireGlobalRoles)) {
    return <>{fallback}</>;
  }
  if (requireJournalRoles.length && !hasJournalRole(journalMemberships, requireJournalRoles)) {
    return <>{fallback}</>;
  }
  if (requireActiveMember && !memberActive && !hasAnyGlobalRole(globalRoles, ["admin"])) {
    return <>{fallback}</>;
  }
  if (
    when &&
    !when({
      globalRoles,
      journalRoles: journalMemberships.map((m) => m.journal_role),
      memberProfile,
      memberActive,
    })
  ) {
    return <>{fallback}</>;
  }
  return <>{children}</>;
}
